import { Component, Input, Inject, ElementRef, ViewChild, HostBinding, HostListener } from '@angular/core';
import { VgAPI, VgUtils } from '../../../va2/core';
import { Config } from '../../universal-config';

const VIDEO_PROPORTION = 9/16;

@Component({
  selector: 'nns-video-player',
  template: `
    <vg-player *ngIf="isBrowser && attachment" (onPlayerReady)="onPlayerReady($event)"
      (onChangeFullscreenEvent)="onChangeFullscreen($event)">
      <overlay-loading *ngIf="isLoading"></overlay-loading>
      <vg-overlay-play></vg-overlay-play>
      <div class="nns-video-wrapper"
        [style.height]="heightPlayer"
        [style.width]="widthPlayer"
      >
        <video [vgMedia]="media" #media #video id="singleVideo" preload="auto">
            <source [src]="fileServerUrl + attachment?.attachmentURL" [type]="attachment?.mime">
        </video>
      </div>
      <vg-controls [ngClass]="{'fullscreen': isFullscreen}">
        <vg-play-pause class="nns-vg-element"></vg-play-pause>

        <vg-time-display vgProperty="current" vgFormat="mm:ss"></vg-time-display>
        <span class="divider">/</span>
        <vg-time-display vgProperty="total" vgFormat="mm:ss"></vg-time-display>

        <vg-scrub-bar>
            <vg-scrub-bar-current-time></vg-scrub-bar-current-time>
            <vg-scrub-bar-buffering-time></vg-scrub-bar-buffering-time>
        </vg-scrub-bar>

        <vg-mute class="nns-vg-element" *ngIf="!isIosDevice"></vg-mute>
        <vg-volume *ngIf="!isIosDevice"></vg-volume>

        <vg-fullscreen class="nns-vg-element"></vg-fullscreen>

        <div class="video-download-button" *ngIf="!isIosDevice">
          <a class="nns-vg-element" [href]="fileServerUrl + attachment?.attachmentURL + '?dw'"
            flex="none" layout="row" layout-align="center center"
          >
            <nns-icon icon="download-player"></nns-icon>
          </a>
        </div>

      </vg-controls>
    </vg-player>
  `,
  styles: [`
    :host {
      display: block;
      box-sizing: border-box;
      position: relative;
      color: white;
      border: 1px solid #dcdcdc;
      padding-bottom: 50px;
      overflow:hidden;
    }

    :host.fullscreen {
      padding-bottom: 0px;
      overflow: visible;
    }

    :host.fullscreen vg-controls {
      bottom: 0px;
    }

    overlay-loading {
      z-index:220;
      display:block;
      width:100%;
      height:100%;
      position:absolute;
    }

    vg-overlay-play {
      width:100%;
      height:100%;
      position:absolute;
      left:0;
      top:0;
    }

    .nns-video-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      margin: 0 auto;
    }

    video {
      position: absolute;
      left: 0;
      height: 100%;
      width: 100%;
    }

    :host.fullscreen .nns-video-wrapper video{
      box-sizing: border-box;
      padding-bottom: 50px;
    }

    :host.fullscreen .vg-overlay-play .overlay-play-container{
      box-sizing: border-box;
      padding-bottom: 50px;
    }

    vg-player {
      overflow: visible;
    }

    vg-controls {
      background-color: #3e4849;
      bottom: -50px;
      left: 0;
      box-sizing:border-box;
      padding:0 8px;
    }

    .nns-vg-element {
      width:auto;
      padding:0 8px;
    }

    vg-scrub-bar {
      bottom: 0;
      background: none;
      margin:0 8px;
    }

    vg-volume {
      padding:0 8px;
    }

    vg-controls.fullscreen {
      bottom: 0;
    }

    .divider {
      display: flex;
      justify-content: center;
      width: 10px;
      user-select: none;
      line-height: 50px;
    }

    .video-download-button {
      display: flex;
      height: 50px;
      justify-content: center;
      line-height: 50px;
      font-family:'Noto Sans', Verdana, Helvetica, sans-serif;
    }

    @media only screen and (max-width: 768px) {
      vg-time-display {
        width:auto;
        padding:0 4px;
      }
      .nns-vg-element,
      vg-controls {
        padding:0 4px;
      }
    }
    @media only screen and (max-width: 600px) {
      vg-time-display {
        font-size:12px;
      }
      vg-volume {
        width:50px;
        padding:0 10px 0 4px;
      }
    }
    @media only screen and (max-width: 480px) {
      vg-volume {display:none;}
    }
  `, `
    :host-context(.nns-theme-night) {
      border-color:rgba(255, 255, 255, 0.12);
    }
  `]
})
export class VideoPlayerComponent {
  @Input() public attachment;
  @Input() public maxHeight;
  @Input() public maxWidth;

  api: VgAPI;
  isLoading = false;
  isBrowser = false;
  heightPlayer = "600px";
  widthPlayer = "100%";
  isIosDevice = false;
  @ViewChild('video') videoRef: ElementRef;
  @HostBinding('class.fullscreen') isFullscreen: boolean = false;
  media;

  subscriptions = [];
  fileServerUrl: string;

  constructor(
    @Inject('isBrowser') isBrowser: boolean,
    private config: Config,
  ) {
    this.isBrowser = isBrowser;
    this.fileServerUrl = this.config.get('BOX_URL')
    if (this.isBrowser) {
      this.isIosDevice = VgUtils.isiOSDevice();
    }
  }

  onPlayerReady(api: VgAPI) {
    this.api = api;
    this.isLoading = !this.api.canPlay;
    this.api.fsAPI.nativeFullscreen = false;

    this.subscriptions.push(this.api.getDefaultMedia().subscriptions.ended.subscribe(
      () => {
        this.api.getDefaultMedia().currentTime = 0;
      }
    ));

    this.subscriptions.push(this.api.getDefaultMedia().subscriptions.canPlay.subscribe(
      () => {
        this.isLoading = false;
      }
    ));

    setTimeout(() => {
      this.isLoading = false;
    }, 2000);

    setTimeout(() => {
      this.resize();
    }, 500);
    this.resize();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnChanges() {
    this.resize();
  }

  resize() {
    if (!this.videoRef) {
      return;
    }
    const videoWidth = this.videoRef.nativeElement.videoWidth;
    const videoHeight = this.videoRef.nativeElement.videoHeight;
    const targetHeight = Number((this.maxWidth*VIDEO_PROPORTION).toFixed(2));
    const targetWidth = Number(((this.maxHeight)/VIDEO_PROPORTION).toFixed(2));

    if (this.isFullscreen) {
      this.heightPlayer = "100%";
      this.widthPlayer = "100%";
    } else {
      if ((targetHeight <= this.maxHeight) || (this.maxHeight === 0)) {
        this.heightPlayer = targetHeight + "px";
        this.widthPlayer = this.maxWidth + "px";
      } else {
        this.heightPlayer = this.maxHeight + "px";
        this.widthPlayer = this.maxWidth + "px";
      }
    }
  }

  @HostListener('document:keydown.esc', ['$event'])
  exitFullscreen() {
    if (this.isFullscreen) {
      this.api.fsAPI.exit();
    }
  }

  onChangeFullscreen(state) {
    if (!state
      && this.videoRef && this.videoRef.nativeElement && this.videoRef.nativeElement.removeAttribute
    ) {
      this.videoRef.nativeElement.removeAttribute('controls');
    }
    this.isFullscreen = state;
    this.resize();
    setTimeout(() => {
      this.resize();
    }, 200);
  }
}
